import { HStack, Stack } from '@chakra-ui/react';
import { createFieldHelper, FormContainer, renderFieldHelperInput, useHookForm } from '@invara-dev/react-base';
import { ApiEndpoints } from '../../../../constants/endpoints/apiEndpoints.ts';
import { ActivityInformationHotelDisplay } from '../displays/activityInformationHotelDisplay';
import { useWatch } from 'react-hook-form';

type HotelSearchForm = {
  HotelIds: number[];
  CityId?: number;
  PointOfInterestResult: string;
};
/**
 * HotelSearch component renders search controls for cities and hotels, and displays matching hotels.
 *
 * It initializes a form with `useHookForm`, sets up dependent select inputs for CityId and HotelIds,
 * and uses `ActivityInformationHotelDisplay` to show results for the selected hotel IDs.
 *
 * @component
 * @returns {JSX.Element} A stack containing city and hotel selectors followed by hotel info cards.
 */
export const HotelSearch = () => {
  const { formMethods } = useHookForm<HotelSearchForm>({ HotelIds: [], PointOfInterestResult: '' });
  const helper = createFieldHelper<HotelSearchForm>(formMethods);

  const [city, hotelIds] = useWatch({
    control: formMethods.control,
    name: ['CityId', 'HotelIds']
  });

  const fields = [
    helper
      .selectInputQuerySearchItems('CityId', {
        searchUrl: ApiEndpoints.selectOptions.cities(),
        minSearchChars: 3
      })
      .withInputProps({ isClearable: true })
      .withLabel('City Name'),

    helper
      .multiSelectQueryItems('HotelIds', {
        searchUrl: ApiEndpoints.selectOptions.hotels.hotelInCity(city ?? 0),
        enabled: city !== undefined && city > 0
      })
      .withInputProps({ isClearable: true })
  ];

  return (
    <Stack>
      <FormContainer methods={formMethods} onSubmit={() => {}} hideControls={true}>
        <HStack>
          {renderFieldHelperInput('CityId', fields)}
          {renderFieldHelperInput('HotelIds', fields)}
        </HStack>
      </FormContainer>
      <ActivityInformationHotelDisplay hotelIds={hotelIds ?? []} />
    </Stack>
  );
};
